<template>
  <div>
    <b-card
      no-body
      class="mb-2"
    >
      <b-card-header class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <feather-icon
            icon="ShuffleIcon"
            size="20"
            class="text-primary mr-1"
          />
          <h4 class="mb-0">
            Hedge Pair Trading
          </h4>
        </div>
        <b-badge :variant="selectedConfig ? 'light-success' : 'light-secondary'">
          {{ selectedConfig ? 'pair seçili' : 'pair seçilmedi' }}
        </b-badge>
      </b-card-header>

      <b-card-body>
        <b-alert
          show
          variant="light-info"
          class="mb-2"
        >
          Bu mod iki farklı coin arasındaki spread/z-score ayrışmasını yönetecek ayrı işlem yüzeyidir.
          Pair tanımı, metrik analizi, entry kararı, paper/live çalışma ve reconciliation aynı yüzeyden yönetilir.
        </b-alert>

        <b-alert
          v-if="feedback.message"
          :variant="feedback.status ? 'success' : 'danger'"
          show
          class="mb-2"
        >
          {{ feedback.message }}
        </b-alert>

        <b-row>
          <b-col
            cols="12"
            lg="4"
            class="mb-2"
          >
            <div class="d-flex align-items-center justify-content-between mb-1">
              <h5 class="mb-0">
                Pair Listesi
              </h5>
              <b-button
                size="sm"
                variant="outline-primary"
                @click="newConfig"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="14"
                />
              </b-button>
            </div>

            <b-input-group class="mb-1">
              <b-form-input
                v-model="search"
                placeholder="ETH, SOL veya pair adı"
                @keyup.enter="fetchList"
              />
              <b-input-group-append>
                <b-button
                  variant="primary"
                  :disabled="loading"
                  @click="fetchList"
                >
                  <feather-icon
                    icon="SearchIcon"
                    size="14"
                  />
                </b-button>
              </b-input-group-append>
            </b-input-group>

            <b-table
              small
              responsive
              show-empty
              :busy="loading"
              :items="configs"
              :fields="configFields"
              empty-text="Pair config yok"
            >
              <template #cell(pair)="data">
                <a
                  href="#"
                  @click.prevent="selectConfig(data.item)"
                >
                  {{ data.item.symbolA }} / {{ data.item.symbolB }}
                </a>
                <div class="font-small-2 text-muted">
                  {{ data.item.name }}
                </div>
              </template>
              <template #cell(mode)="data">
                <b-badge :variant="data.item.mode === 'LIVE' ? 'light-danger' : 'light-info'">
                  {{ data.item.mode === 'LIVE' ? 'Live' : 'Paper' }}
                </b-badge>
              </template>
              <template #cell(enabled)="data">
                <b-badge :variant="data.item.enabled ? 'light-success' : 'light-secondary'">
                  {{ data.item.enabled ? 'aktif' : 'pasif' }}
                </b-badge>
              </template>
            </b-table>
          </b-col>

          <b-col
            cols="12"
            lg="8"
            class="mb-2"
          >
            <div class="d-flex align-items-center justify-content-between mb-1">
              <h5 class="mb-0">
                Pair Builder
              </h5>
              <div>
                <b-button
                  size="sm"
                  variant="outline-danger"
                  class="mr-50"
                  :disabled="!selectedConfig || saving"
                  @click="archiveConfig"
                >
                  <feather-icon
                    icon="ArchiveIcon"
                    size="14"
                  />
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-primary"
                  class="mr-50"
                  :disabled="saving"
                  @click="analyzeConfig"
                >
                  <feather-icon
                    icon="ActivityIcon"
                    size="14"
                    class="mr-50"
                  />
                  Ön analiz
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-success"
                  class="mr-50"
                  :disabled="!selectedConfig || paperRunning"
                  @click="runPaper"
                >
                  <feather-icon
                    icon="PlayCircleIcon"
                    size="14"
                    class="mr-50"
                  />
                  Paper çalıştır
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-info"
                  class="mr-50"
                  :disabled="backtestRunning"
                  @click="runBacktest"
                >
                  <feather-icon
                    icon="BarChart2Icon"
                    size="14"
                    class="mr-50"
                  />
                  Backtest
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  class="mr-50"
                  :disabled="!selectedConfig || paperRunning"
                  @click="reconcilePaper"
                >
                  <feather-icon
                    icon="RefreshCwIcon"
                    size="14"
                    class="mr-50"
                  />
                  Recon
                </b-button>
                <b-button
                  v-if="form.mode === 'LIVE'"
                  size="sm"
                  variant="outline-warning"
                  class="mr-50"
                  :disabled="!selectedConfig || livePreflightRunning"
                  @click="runLivePreflight"
                >
                  <feather-icon
                    icon="ShieldIcon"
                    size="14"
                    class="mr-50"
                  />
                  Live preflight
                </b-button>
                <b-button
                  size="sm"
                  variant="primary"
                  :disabled="saving"
                  @click="saveConfig"
                >
                  <feather-icon
                    icon="SaveIcon"
                    size="14"
                    class="mr-50"
                  />
                  Kaydet
                </b-button>
              </div>
            </div>

            <b-row class="mb-1">
              <b-col
                cols="12"
                md="3"
              >
                <b-form-group label="Analiz provider">
                  <b-form-select
                    v-model="analyzeProviderKind"
                    :options="providerOptions"
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="3"
              >
                <b-form-group label="Mum limiti">
                  <b-form-input
                    v-model.number="analyzeCandleLimit"
                    type="number"
                    min="50"
                    max="1500"
                    placeholder="Otomatik"
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="3"
              >
                <b-form-group label="Backtest başlangıç">
                  <b-form-input
                    v-model="backtestStartAt"
                    type="date"
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="3"
              >
                <b-form-group label="Backtest bitiş">
                  <b-form-input
                    v-model="backtestEndAt"
                    type="date"
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="12"
                class="d-flex align-items-center"
              >
                <small class="text-muted">
                  Limit boşsa lookback ve z-score ihtiyacına göre otomatik seçilir. Tarih aralığı boşsa son mumlar test edilir.
                </small>
              </b-col>
            </b-row>

            <b-tabs content-class="pt-2">
              <b-tab
                title="Temel"
                active
              >
                <b-row>
                  <b-col
                    cols="12"
                    md="6"
                  >
                    <b-form-group label="Ad">
                      <b-form-input v-model="form.name" />
                    </b-form-group>
                  </b-col>
                  <b-col
                    cols="12"
                    md="3"
                  >
                    <b-form-group label="Mod">
                      <b-form-select
                        v-model="form.mode"
                        :options="modeOptions"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col
                    cols="12"
                    md="3"
                  >
                    <b-form-group label="Durum">
                      <b-form-checkbox
                        v-model="form.enabled"
                        switch
                      >
                        Aktif
                      </b-form-checkbox>
                    </b-form-group>
                  </b-col>
                  <b-col
                    cols="12"
                    md="4"
                  >
                    <b-form-group label="Coin A">
                      <b-form-input
                        v-model="form.symbolA"
                        placeholder="ETHUSDT"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col
                    cols="12"
                    md="4"
                  >
                    <b-form-group label="Coin B">
                      <b-form-input
                        v-model="form.symbolB"
                        placeholder="SOLUSDT"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col
                    cols="12"
                    md="4"
                  >
                    <b-form-group label="Timeframe">
                      <b-form-input
                        v-model="form.timeframe"
                        placeholder="5m"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col
                    v-if="form.mode === 'LIVE'"
                    cols="12"
                    md="6"
                  >
                    <b-form-group label="Live API Key ID">
                      <b-form-input
                        v-model.number="form.apiKeyId"
                        type="number"
                        min="1"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col
                    v-if="form.mode === 'LIVE'"
                    cols="12"
                    md="6"
                    class="d-flex align-items-center"
                  >
                    <b-form-checkbox
                      v-model="form.acknowledgeLiveRisk"
                      switch
                    >
                      Live emir riskini onaylıyorum
                    </b-form-checkbox>
                  </b-col>
                </b-row>
              </b-tab>

              <b-tab title="Ölçüm">
                <b-row>
                  <b-col
                    v-for="field in measurementFields"
                    :key="field.key"
                    cols="12"
                    md="3"
                  >
                    <b-form-group :label="field.label">
                      <b-form-input
                        v-model.number="form[field.key]"
                        type="number"
                        :min="field.min"
                        :max="field.max"
                        :step="field.step || 1"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-tab>

              <b-tab title="Risk ve Sizing">
                <b-row>
                  <b-col
                    cols="12"
                    md="4"
                  >
                    <b-form-group label="Pair sermayesi USDT">
                      <b-form-input
                        v-model.number="form.totalPairCapitalUsdt"
                        type="number"
                        min="1"
                        step="1"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col
                    cols="12"
                    md="4"
                  >
                    <b-form-group label="Sizing yöntemi">
                      <b-form-select
                        v-model="form.sizingMethod"
                        :options="sizingOptions"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col
                    cols="12"
                    md="4"
                  >
                    <b-form-group label="Margin modu">
                      <b-form-select
                        v-model="form.marginMode"
                        :options="marginOptions"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col
                    v-for="field in riskFields"
                    :key="field.key"
                    cols="12"
                    md="3"
                  >
                    <b-form-group :label="field.label">
                      <b-form-input
                        v-model.number="form[field.key]"
                        type="number"
                        :min="field.min"
                        :max="field.max"
                        :step="field.step || 1"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-tab>
            </b-tabs>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-row>
      <b-col
        cols="12"
        lg="4"
      >
        <b-card>
          <h5>Canlı Pair Kartı</h5>
          <div v-if="latestTrade">
            <div class="d-flex justify-content-between mb-50">
              <span class="text-muted">Durum</span>
              <b-badge :variant="tradeStatusVariant(latestTrade.status)">
                {{ latestTrade.status }}
              </b-badge>
            </div>
            <div class="d-flex justify-content-between mb-50">
              <span class="text-muted">Pair</span>
              <strong>{{ latestTrade.pairKey }}</strong>
            </div>
            <div class="d-flex justify-content-between mb-50">
              <span class="text-muted">Net PnL</span>
              <strong :class="pnlClass(latestTrade.estimatedNetPnlUsdt || latestTrade.realizedPnlUsdt)">
                {{ money(latestTrade.estimatedNetPnlUsdt || latestTrade.realizedPnlUsdt) }}
              </strong>
            </div>
            <div
              v-if="canCloseLatestTrade"
              class="d-flex align-items-center mt-1"
            >
              <b-button
                size="sm"
                variant="outline-warning"
                class="mr-50"
                :disabled="safeClosing"
                @click="safeClosePair(false)"
              >
                <feather-icon
                  icon="ShieldOffIcon"
                  size="14"
                  class="mr-50"
                />
                Güvenli kapat
              </b-button>
              <b-button
                size="sm"
                variant="outline-danger"
                :disabled="safeClosing"
                @click="safeClosePair(true)"
              >
                <feather-icon
                  icon="AlertTriangleIcon"
                  size="14"
                  class="mr-50"
                />
                Acil kapat
              </b-button>
            </div>
            <div
              v-for="leg in latestTrade.legs"
              :key="leg.id"
              class="border-top pt-50 mt-50"
            >
              <div class="d-flex justify-content-between">
                <span>{{ leg.symbol }} · {{ leg.side }}</span>
                <strong :class="pnlClass(leg.unrealizedPnlUsdt || leg.realizedPnlUsdt)">
                  {{ money(leg.unrealizedPnlUsdt || leg.realizedPnlUsdt) }}
                </strong>
              </div>
              <small class="text-muted">
                qty {{ formatNumber(leg.quantity, 6) }} · entry {{ formatNumber(leg.entryPrice, 4) }} · now {{ formatNumber(leg.currentPrice, 4) }}
              </small>
            </div>
          </div>
          <p
            v-else
            class="text-muted mb-0"
          >
            Henüz paper trade yok.
          </p>
        </b-card>
      </b-col>
      <b-col
        cols="12"
        lg="4"
      >
        <b-card>
          <h5>Son Ön Analiz</h5>
          <div v-if="lastAnalysis">
            <b-badge :variant="lastAnalysis.ready ? 'light-success' : 'light-warning'">
              {{ lastAnalysis.reason }}
            </b-badge>
            <p class="text-muted mt-1 mb-0">
              {{ lastAnalysis.message }}
            </p>
            <div
              v-if="entryDecisionRows.length"
              class="mt-1"
            >
              <div
                v-for="row in entryDecisionRows"
                :key="row.key"
                class="d-flex justify-content-between border-bottom py-25"
              >
                <span class="text-muted">{{ row.label }}</span>
                <strong>{{ row.value }}</strong>
              </div>
            </div>
            <div class="mt-1">
              <div
                v-for="metric in metricRows"
                :key="metric.key"
                class="d-flex justify-content-between border-bottom py-25"
              >
                <span class="text-muted">{{ metric.label }}</span>
                <strong>{{ metric.value }}</strong>
              </div>
            </div>
          </div>
          <p
            v-else
            class="text-muted mb-0"
          >
            Henüz ön analiz çalışmadı.
          </p>
        </b-card>
      </b-col>
      <b-col
        cols="12"
        lg="4"
      >
        <b-card>
          <h5>Sağlık ve Block Reason</h5>
          <div
            v-if="reconciliationResult"
            class="mb-1"
          >
            <div class="d-flex justify-content-between mb-50">
              <span class="text-muted">Recon</span>
              <b-badge :variant="reconciliationResult.healthy ? 'light-success' : 'light-danger'">
                {{ reconciliationResult.healthy ? 'sağlıklı' : 'recovery' }}
              </b-badge>
            </div>
            <div class="d-flex justify-content-between mb-50">
              <span class="text-muted">Aksiyon</span>
              <strong>{{ reconciliationResult.action }}</strong>
            </div>
            <div
              v-if="reconciliationResult.preflight"
              class="mt-50"
            >
              <small
                v-for="item in reconciliationResult.preflight.symbolMeta || []"
                :key="item.symbol"
                class="text-muted d-block"
              >
                {{ item.symbol }} qty {{ formatNumber(item.quantity, 6) }} · notional {{ money(item.notionalUsdt) }} · lev {{ item.leverage }}x
              </small>
            </div>
            <small class="text-muted d-block">
              {{ reconciliationResult.message }}
            </small>
          </div>
          <ul
            v-if="healthReasonRows.length"
            class="pl-1 mb-0"
          >
            <li
              v-for="reason in healthReasonRows"
              :key="reason"
            >
              {{ reasonLabel(reason) }}
            </li>
          </ul>
          <ul
            v-else
            class="pl-1 mb-0"
          >
            <li>Live mod API key ve risk onayı ister.</li>
            <li>Varsayılan güvenli recovery kalan bacağı kapatır.</li>
            <li>İlk sürüm paper trading ile doğrulanmadan live açmaz.</li>
          </ul>
        </b-card>
      </b-col>
    </b-row>

    <b-card
      v-if="runtimeEvents.length"
      class="mt-2"
    >
      <h5>Paper Event Timeline</h5>
      <b-table
        small
        responsive
        :items="runtimeEvents"
        :fields="eventFields"
      >
        <template #cell(createdAt)="data">
          {{ formatDate(data.item.createdAt) }}
        </template>
        <template #cell(reason)="data">
          <b-badge variant="light-info">
            {{ data.item.reason }}
          </b-badge>
        </template>
      </b-table>
    </b-card>

    <b-card
      v-if="backtestResult"
      class="mt-2"
    >
      <div class="d-flex align-items-center justify-content-between mb-1">
        <h5 class="mb-0">
          Backtest Raporu
        </h5>
        <b-badge variant="light-info">
          {{ backtestResult.summary.totalTrades }} trade
        </b-badge>
      </div>
      <b-row class="mb-1">
        <b-col
          v-for="metric in backtestSummaryRows"
          :key="metric.key"
          cols="6"
          md="3"
          class="mb-1"
        >
          <small class="text-muted d-block">{{ metric.label }}</small>
          <strong :class="metric.pnl ? pnlClass(metric.raw) : ''">{{ metric.value }}</strong>
        </b-col>
      </b-row>
      <b-table
        small
        responsive
        :items="backtestResult.trades || []"
        :fields="backtestFields"
        empty-text="Trade yok"
        show-empty
      >
        <template #cell(entryAt)="data">
          {{ formatDate(data.item.entryAt) }}
        </template>
        <template #cell(exitAt)="data">
          {{ formatDate(data.item.exitAt) }}
        </template>
        <template #cell(direction)="data">
          <b-badge variant="light-primary">
            {{ data.item.direction }}
          </b-badge>
        </template>
        <template #cell(netPnlUsdt)="data">
          <strong :class="pnlClass(data.item.netPnlUsdt)">
            {{ money(data.item.netPnlUsdt) }}
          </strong>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCol,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BInputGroup,
  BInputGroupAppend,
  BRow,
  BTab,
  BTable,
  BTabs,
} from 'bootstrap-vue'
import axiosIns from '@/libs/axios'

export default {
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCol,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BInputGroup,
    BInputGroupAppend,
    BRow,
    BTab,
    BTable,
    BTabs,
  },
  data() {
    return {
      loading: false,
      saving: false,
      search: '',
      configs: [],
      selectedConfig: null,
      lastAnalysis: null,
      reconciliationResult: null,
      paperRunning: false,
      backtestRunning: false,
      livePreflightRunning: false,
      safeClosing: false,
      runtimeTrades: [],
      runtimeEvents: [],
      backtestResult: null,
      backtestStartAt: '',
      backtestEndAt: '',
      analyzeProviderKind: 'BINANCE',
      analyzeCandleLimit: null,
      feedback: { status: true, message: '' },
      configFields: [
        { key: 'pair', label: 'Pair' },
        { key: 'mode', label: 'Mod' },
        { key: 'enabled', label: 'Durum' },
      ],
      eventFields: [
        { key: 'createdAt', label: 'Zaman' },
        { key: 'decision', label: 'Karar' },
        { key: 'reason', label: 'Reason' },
      ],
      backtestFields: [
        { key: 'entryAt', label: 'Giriş' },
        { key: 'exitAt', label: 'Çıkış' },
        { key: 'direction', label: 'Yön' },
        { key: 'entryZScore', label: 'Entry Z' },
        { key: 'exitZScore', label: 'Exit Z' },
        { key: 'durationMinutes', label: 'Dk' },
        { key: 'netPnlUsdt', label: 'Net PnL' },
        { key: 'closeReason', label: 'Sebep' },
      ],
      modeOptions: [
        { value: 'PAPER', text: 'Paper' },
        { value: 'LIVE', text: 'Live' },
      ],
      sizingOptions: [
        { value: 'REGRESSION_HEDGE_RATIO', text: 'Regression hedge ratio' },
        { value: 'EQUAL_NOTIONAL', text: 'Equal notional' },
        { value: 'BETA_ADJUSTED', text: 'Beta adjusted' },
        { value: 'VOLATILITY_ADJUSTED', text: 'Volatility adjusted' },
      ],
      marginOptions: [
        { value: 'ISOLATED', text: 'Isolated' },
        { value: 'CROSSED', text: 'Cross' },
      ],
      providerOptions: [
        { value: 'BINANCE', text: 'Binance' },
        { value: 'BINANCE_TESTNET', text: 'Binance Testnet' },
        { value: 'SIMULATED', text: 'Simulated' },
      ],
      measurementFields: [
        {
          key: 'lookback', label: 'Lookback', min: 50, max: 2000,
        },
        {
          key: 'correlationLookback', label: 'Korelasyon LB', min: 30, max: 2000,
        },
        {
          key: 'regressionLookback', label: 'Regresyon LB', min: 30, max: 2000,
        },
        {
          key: 'zscoreLookback', label: 'Z-score LB', min: 30, max: 2000,
        },
        {
          key: 'entryZScore', label: 'Entry Z', min: 0.1, max: 10, step: 0.1,
        },
        {
          key: 'exitZScore', label: 'Exit Z', min: 0, max: 5, step: 0.1,
        },
        {
          key: 'stopZScore', label: 'Stop Z', min: 0.1, max: 20, step: 0.1,
        },
        {
          key: 'minCorrelation', label: 'Min korelasyon', min: -1, max: 1, step: 0.05,
        },
      ],
      riskFields: [
        {
          key: 'defaultLeverage', label: 'Varsayılan kaldıraç', min: 1, max: 125,
        },
        {
          key: 'maxNetLossUsdt', label: 'Max net zarar', min: 1, max: 1000000, step: 1,
        },
        {
          key: 'maxOpenMinutes', label: 'Max açık dk', min: 1, max: 43200,
        },
        {
          key: 'cooldownMinutes', label: 'Cooldown dk', min: 0, max: 43200,
        },
        {
          key: 'minPairStability', label: 'Min stabilite', min: 0, max: 1, step: 0.05,
        },
        {
          key: 'maxFundingCostUsdt', label: 'Max funding USDT', min: 0, max: 1000000, step: 0.1,
        },
        {
          key: 'confirmationBars', label: 'Onay mum', min: 1, max: 20,
        },
        {
          key: 'reconciliationIntervalSeconds', label: 'Recon sn', min: 10, max: 3600,
        },
      ],
      form: {},
    }
  },
  computed: {
    latestTrade() {
      return this.runtimeTrades[0] || null
    },
    canCloseLatestTrade() {
      return Boolean(this.selectedConfig && this.latestTrade && ['OPEN', 'RECOVERY', 'OPENING_LEG_A', 'OPENING_LEG_B'].includes(this.latestTrade.status))
    },
    metricRows() {
      const metrics = this.lastAnalysis?.metrics || {}
      const coverage = this.lastAnalysis?.coverage || {}
      return [
        { key: 'correlation', label: 'Korelasyon', value: this.formatNumber(metrics.correlation, 4) },
        { key: 'hedgeRatio', label: 'Hedge ratio', value: this.formatNumber(metrics.hedgeRatio, 4) },
        { key: 'beta', label: 'Beta', value: this.formatNumber(metrics.beta, 4) },
        { key: 'volatilityRatio', label: 'Vol oranı', value: this.formatNumber(metrics.volatilityRatio, 4) },
        { key: 'spread', label: 'Spread', value: this.formatNumber(metrics.spread, 6) },
        { key: 'zScore', label: 'Z-score', value: this.formatNumber(metrics.zScore, 3) },
        { key: 'stability', label: 'Stability', value: this.formatNumber(metrics.pairStabilityScore, 3) },
        { key: 'coverage', label: 'Veri kapsamı', value: `${coverage.aligned || 0}/${this.lastAnalysis?.requiredHistory || 0}` },
      ]
    },
    entryDecisionRows() {
      const decision = this.lastAnalysis?.entryDecision
      if (!decision) return []
      const legs = decision.sizing?.legs || []
      const legPlan = legs.length
        ? legs.map(leg => `${leg.symbol} ${leg.side} ${this.moneyNoSign(leg.marginUsdt)} margin`).join(' + ')
        : '—'
      return [
        { key: 'entryAction', label: 'Entry kararı', value: decision.allowed ? 'Açılabilir' : 'Bekle / blok' },
        { key: 'entryReason', label: 'Entry reason', value: this.reasonLabel(decision.reason) },
        { key: 'entryDirection', label: 'Yön', value: decision.direction || '—' },
        { key: 'entryPlan', label: 'Bacak planı', value: legPlan },
      ]
    },
    reasonRows() {
      const reasons = this.lastAnalysis?.reasons || []
      return reasons.filter(reason => reason !== 'CONFIG_VALIDATED')
    },
    healthReasonRows() {
      const reconReasons = (this.reconciliationResult?.reasons || [])
        .filter(reason => reason !== 'CONFIG_VALIDATED')
      return reconReasons.length ? reconReasons : this.reasonRows
    },
    backtestSummaryRows() {
      const summary = this.backtestResult?.summary || {}
      return [
        {
          key: 'net',
          label: 'Net PnL',
          value: this.money(summary.netPnlUsdt),
          raw: summary.netPnlUsdt,
          pnl: true,
        },
        {
          key: 'wr',
          label: 'Win Rate',
          value: `${this.formatNumber((summary.winRate || 0) * 100, 2)}%`,
        },
        {
          key: 'pf',
          label: 'Profit Factor',
          value: this.formatNumber(summary.profitFactor, 3),
        },
        {
          key: 'dd',
          label: 'Max DD',
          value: this.money(-(summary.maxDrawdownUsdt || 0)),
          raw: -(summary.maxDrawdownUsdt || 0),
          pnl: true,
        },
        {
          key: 'cost',
          label: 'Komisyon + slip',
          value: this.money(-((summary.totalCommissionUsdt || 0) + (summary.totalSlippageUsdt || 0))),
          raw: -((summary.totalCommissionUsdt || 0) + (summary.totalSlippageUsdt || 0)),
          pnl: true,
        },
        {
          key: 'mean',
          label: 'Mean revert',
          value: this.formatNumber(summary.meanReversionCount, 0),
        },
        {
          key: 'stop',
          label: 'Stop',
          value: this.formatNumber((summary.stopCount || 0) + (summary.maxLossCount || 0), 0),
        },
        {
          key: 'duration',
          label: 'Ort. süre',
          value: `${this.formatNumber(summary.avgTradeMinutes, 1)} dk`,
        },
      ]
    },
  },
  created() {
    this.form = this.emptyForm()
    this.fetchList()
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        name: 'Yeni hedge pair',
        enabled: false,
        mode: 'PAPER',
        apiKeyId: null,
        acknowledgeLiveRisk: false,
        symbolA: 'ETHUSDT',
        symbolB: 'SOLUSDT',
        market: 'FUTURES',
        timeframe: '5m',
        lookback: 300,
        correlationLookback: 200,
        regressionLookback: 200,
        zscoreLookback: 100,
        totalPairCapitalUsdt: 40,
        defaultLeverage: 3,
        leverageA: null,
        leverageB: null,
        marginMode: 'ISOLATED',
        sizingMethod: 'REGRESSION_HEDGE_RATIO',
        entryZScore: 2,
        exitZScore: 0.4,
        stopZScore: 3,
        minCorrelation: 0.7,
        minPairStability: 0.6,
        maxSpreadVelocity: null,
        confirmationBars: 1,
        maxOpenMinutes: 240,
        maxNetLossUsdt: 6,
        maxFundingCostUsdt: 0,
        cooldownMinutes: 60,
        reconciliationIntervalSeconds: 60,
        recoveryPolicy: 'CLOSE_REMAINING_LEG',
        riskConfig: {
          maxOpenPairs: 1,
          dailyMaxLossUsdt: 0,
          totalCapitalLimitUsdt: 0,
          maxGrossExposureUsdt: 0,
          maxNetExposureUsdt: 0,
          maxCoinReuse: 1,
          minLiquidationDistancePct: 5,
        },
      }
    },
    async fetchList() {
      this.loading = true
      try {
        const response = await axiosIns.post('/hedge-pair-trading', {
          search: this.search || undefined,
          page: 1,
          limit: 50,
        })
        this.configs = response.data.data || []
      } catch (error) {
        this.showError(error)
      } finally {
        this.loading = false
      }
    },
    newConfig() {
      this.selectedConfig = null
      this.lastAnalysis = null
      this.reconciliationResult = null
      this.backtestResult = null
      this.form = this.emptyForm()
    },
    selectConfig(config) {
      this.selectedConfig = config
      this.lastAnalysis = config.lastAnalysis || null
      this.reconciliationResult = null
      this.backtestResult = null
      this.form = {
        ...this.emptyForm(),
        ...config,
        acknowledgeLiveRisk: config.mode === 'LIVE',
      }
      this.fetchRuntimeTables()
    },
    async saveConfig() {
      this.saving = true
      try {
        const payload = this.payload()
        const response = this.form.id
          ? await axiosIns.patch(`/hedge-pair-trading/${this.form.id}`, payload)
          : await axiosIns.post('/hedge-pair-trading/create', payload)
        this.selectedConfig = response.data.data
        this.form = {
          ...this.emptyForm(),
          ...response.data.data,
          acknowledgeLiveRisk: response.data.data.mode === 'LIVE',
        }
        this.feedback = { status: true, message: response.data.message || 'Kaydedildi' }
        await this.fetchList()
      } catch (error) {
        this.showError(error)
      } finally {
        this.saving = false
      }
    },
    async analyzeConfig() {
      this.saving = true
      try {
        const body = this.form.id
          ? this.analyzeBody({ configId: this.form.id })
          : this.analyzeBody({ config: this.payload() })
        const response = await axiosIns.post('/hedge-pair-trading/analyze', body)
        this.lastAnalysis = response.data.data
        this.feedback = { status: true, message: response.data.message || 'Ön analiz tamamlandı' }
        if (this.form.id) await this.fetchList()
      } catch (error) {
        this.showError(error)
      } finally {
        this.saving = false
      }
    },
    async runPaper() {
      if (!this.selectedConfig) return
      this.paperRunning = true
      try {
        const response = await axiosIns.post(`/hedge-pair-trading/${this.selectedConfig.id}/paper-run`, this.analyzeBody({}))
        const result = response.data.data
        this.lastAnalysis = result.analysis
        this.reconciliationResult = null
        this.runtimeTrades = result.trade ? [result.trade] : []
        this.runtimeEvents = result.events || []
        this.feedback = { status: true, message: `${response.data.message || 'Paper çalıştı'} · ${result.action}` }
        await this.fetchRuntimeTables()
      } catch (error) {
        this.showError(error)
      } finally {
        this.paperRunning = false
      }
    },
    async runBacktest() {
      this.backtestRunning = true
      try {
        const body = this.form.id
          ? { configId: this.form.id }
          : { config: this.payload() }
        const response = await axiosIns.post('/hedge-pair-trading/backtest', {
          ...body,
          providerKind: this.analyzeProviderKind,
          candleLimit: this.analyzeCandleLimit || undefined,
          startAt: this.dateToStartIso(this.backtestStartAt),
          endAt: this.dateToEndIso(this.backtestEndAt),
          forceCloseAtEnd: true,
        })
        this.backtestResult = response.data.data
        this.feedback = { status: true, message: response.data.message || 'Backtest tamamlandı' }
      } catch (error) {
        this.showError(error)
      } finally {
        this.backtestRunning = false
      }
    },
    async reconcilePaper() {
      if (!this.selectedConfig) return
      this.paperRunning = true
      try {
        const response = await axiosIns.post(`/hedge-pair-trading/${this.selectedConfig.id}/reconcile`, {
          providerKind: this.analyzeProviderKind,
        })
        const result = response.data.data
        this.reconciliationResult = result
        if (result.trade) this.runtimeTrades = [result.trade]
        this.runtimeEvents = result.events || []
        this.feedback = { status: result.healthy, message: result.message || response.data.message }
        await this.fetchRuntimeTables()
      } catch (error) {
        this.showError(error)
      } finally {
        this.paperRunning = false
      }
    },
    async runLivePreflight() {
      if (!this.selectedConfig) return
      this.livePreflightRunning = true
      try {
        const response = await axiosIns.post(`/hedge-pair-trading/${this.selectedConfig.id}/live-run`, {
          providerKind: this.analyzeProviderKind === 'SIMULATED' ? undefined : this.analyzeProviderKind,
          candleLimit: this.analyzeCandleLimit || undefined,
          dryRun: true,
          acknowledgeLiveExecution: false,
        })
        const result = response.data.data
        this.lastAnalysis = result.analysis
        this.reconciliationResult = {
          healthy: result.preflight?.ok !== false,
          action: result.action,
          message: result.preflight?.ok
            ? 'Live preflight başarılı; dry-run olduğu için emir gönderilmedi.'
            : 'Live preflight uyarı üretti.',
          reasons: result.preflight?.ok ? ['CONFIG_VALIDATED'] : ['RISK_LIMIT'],
          preflight: result.preflight,
        }
        this.runtimeEvents = result.events || []
        this.feedback = { status: true, message: response.data.message || 'Live preflight tamamlandı' }
      } catch (error) {
        this.showError(error)
      } finally {
        this.livePreflightRunning = false
      }
    },
    async safeClosePair(emergency) {
      if (!this.selectedConfig || !this.latestTrade) return
      // eslint-disable-next-line no-alert
      if (emergency && !window.confirm('Acil kapatma iki bacağı birlikte kapatmayı dener. Devam edilsin mi?')) return
      this.safeClosing = true
      try {
        const endpoint = emergency ? 'emergency-close' : 'safe-close'
        const response = await axiosIns.post(`/hedge-pair-trading/${this.selectedConfig.id}/${endpoint}`, {
          pairTradeId: this.latestTrade.id,
          reason: emergency ? 'ui_emergency_close' : 'ui_safe_close',
          acknowledgeLiveCloseRisk: this.form.mode === 'LIVE',
        })
        const result = response.data.data
        this.runtimeTrades = result.trade ? [result.trade] : []
        this.runtimeEvents = result.events || []
        this.feedback = { status: result.action === 'CLOSED', message: `${response.data.message || 'Kapatma çalıştı'} · ${result.action}` }
      } catch (error) {
        this.showError(error)
      } finally {
        this.safeClosing = false
      }
    },
    async fetchRuntimeTables() {
      if (!this.selectedConfig) return
      try {
        const [tradesResponse, eventsResponse] = await Promise.all([
          axiosIns.get(`/hedge-pair-trading/${this.selectedConfig.id}/trades`),
          axiosIns.get(`/hedge-pair-trading/${this.selectedConfig.id}/events`),
        ])
        this.runtimeTrades = tradesResponse.data.data || []
        this.runtimeEvents = eventsResponse.data.data || []
      } catch (error) {
        this.showError(error)
      }
    },
    async archiveConfig() {
      if (!this.selectedConfig) return
      this.saving = true
      try {
        await axiosIns.delete(`/hedge-pair-trading/${this.selectedConfig.id}`)
        this.feedback = { status: true, message: 'Pair config arşivlendi' }
        this.newConfig()
        await this.fetchList()
      } catch (error) {
        this.showError(error)
      } finally {
        this.saving = false
      }
    },
    payload() {
      const payload = {
        ...this.form,
        symbolA: String(this.form.symbolA || '').toUpperCase(),
        symbolB: String(this.form.symbolB || '').toUpperCase(),
      }
      delete payload.id
      delete payload.createdAt
      delete payload.updatedAt
      delete payload.lastAnalysis
      delete payload.lastAnalysisAt
      if (payload.mode !== 'LIVE') {
        payload.apiKeyId = null
        payload.acknowledgeLiveRisk = false
      }
      return payload
    },
    analyzeBody(base) {
      return {
        ...base,
        providerKind: this.analyzeProviderKind,
        candleLimit: this.analyzeCandleLimit || undefined,
      }
    },
    formatNumber(value, digits = 2) {
      const numberValue = Number(value)
      if (!Number.isFinite(numberValue)) return '—'
      return numberValue.toLocaleString('tr-TR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: digits,
      })
    },
    money(value) {
      const numberValue = Number(value)
      if (!Number.isFinite(numberValue)) return '—'
      return `${numberValue >= 0 ? '+' : ''}${numberValue.toLocaleString('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} USDT`
    },
    moneyNoSign(value) {
      const numberValue = Number(value)
      if (!Number.isFinite(numberValue)) return '—'
      return `${numberValue.toLocaleString('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} USDT`
    },
    pnlClass(value) {
      const numberValue = Number(value)
      if (!Number.isFinite(numberValue) || numberValue === 0) return 'text-muted'
      return numberValue > 0 ? 'text-success' : 'text-danger'
    },
    tradeStatusVariant(status) {
      if (status === 'OPEN') return 'light-success'
      if (status === 'RECOVERY' || status === 'FAILED') return 'light-danger'
      if (status === 'CLOSED') return 'light-secondary'
      return 'light-warning'
    },
    formatDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleString('tr-TR')
    },
    dateToStartIso(value) {
      if (!value) return undefined
      return new Date(`${value}T00:00:00.000`).toISOString()
    },
    dateToEndIso(value) {
      if (!value) return undefined
      return new Date(`${value}T23:59:59.999`).toISOString()
    },
    reasonLabel(reason) {
      const labels = {
        INSUFFICIENT_HISTORY: 'Yeterli senkron mum geçmişi yok.',
        INVALID_PRICE: 'Mum verisinde geçersiz fiyat var.',
        UNSYNCED_CANDLES: 'İki coin mum zamanları tam senkron değil.',
        ZERO_STD_DEV: 'Varyans sıfır; z-score üretilemiyor.',
        LOW_CORRELATION: 'Korelasyon minimum eşiğin altında.',
        UNSTABLE_PAIR: 'Pair stability minimum eşiğin altında.',
        ZSCORE_NOT_REACHED: 'Z-score entry eşiğine ulaşmadı.',
        ENTRY_ZSCORE_REACHED: 'Z-score entry eşiğine ulaştı.',
        EXIT_MEAN_REVERSION: 'Spread mean reversion bölgesine döndü.',
        MAX_LOSS_REACHED: 'Maksimum zarar eşiği tetiklendi.',
        MAX_DURATION_REACHED: 'Maksimum açık kalma süresi doldu.',
        ADVERSE_ZSCORE_REACHED: 'Z-score ters yönde stop bölgesine ilerledi.',
        PAIR_ALREADY_OPEN: 'Bu pair üzerinde aktif işlem var.',
        RISK_LIMIT: 'Risk limiti işlemi engelledi.',
        COOLDOWN: 'Pair cooldown süresinde.',
        LEG_OPEN_FAILED: 'Pair bacaklarından biri açılamadı.',
        MISSING_LOCAL_POSITION: 'Lokal pair bacağı eksik.',
        MISSING_EXCHANGE_POSITION: 'Exchange tarafında beklenen pozisyon yok.',
        QUANTITY_MISMATCH: 'Lokal ve exchange miktarı uyuşmuyor.',
        ORPHAN_ORDER: 'Sahipsiz emir tespit edildi.',
        ORPHAN_LEG: 'Sahipsiz bacak tespit edildi.',
        DUPLICATE_FILL: 'Duplicate fill veya tekrar işlenmiş bacak var.',
        UNEXPECTED_MANUAL_CLOSE: 'Manuel veya dış kapanış tespit edildi.',
        EXTERNAL_POSITION_CHANGE: 'Exchange tarafında dış pozisyon değişikliği var.',
        POSITION_MISMATCH: 'Pair pozisyon durumu uyuşmuyor.',
      }
      return labels[reason] || reason
    },
    showError(error) {
      const message = error?.response?.data?.message
        || Object.values(error?.response?.data?.errors || {}).flat().join(' · ')
        || error.message
        || 'İşlem başarısız'
      this.feedback = { status: false, message }
    },
  },
}
</script>
